import { useState, useCallback } from "react";
import {
  useRecordContext,
  useGetList,
  useList,
  useNotify,
  FunctionField,
  TextField,
  ListContextProvider,
  SelectColumnsButton,
} from "react-admin";
import { Box, Typography, IconButton, Tooltip, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import { TabContent } from "@/components/common/PageLayout";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { DeductibleSourceChip } from "@/components/ui/mui/DeductibleSourceChip";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatSignedUSD, formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { downloadLaseredClaimantsReport } from "@/utils/exporters/laseredClaimantsExporter";
import { ContractLasersSummaryCard } from "./ContractLasersSummaryCard";
import { LaserChipWithReview } from "@/components/common/DeductibleReviewFlag";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { ColorType, ConfirmLabelType, ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import Button from "@/components/ui/Button";
import { LaseredClaimantDialog } from "./LaseredClaimantDialog";
import { REMOVE_LASER_OUTCOME, RemoveLaserSummary } from "./RemoveLaserSummary";
import { removeLaseredClaimant } from "../hooks/useLaseredConditions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { useRecomputeRefreshOrStandalone } from "@/resources/claimants/hooks/useRecomputeRefresh";
import { AlertType, hasStraightLaser, type LaseredClaimantListItem, type Policy } from "@/types";
import { hasContractLaser, laserKindLabel } from "@/utils/helpers/laserKind";
import { conditionStateCountsText } from "@/utils/helpers/expectedTrigger";

const NO_DECIMALS = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

/** The claimant's display name on a row. */
const rowName = (r: LaseredClaimantListItem): string =>
  [r.claimant_first_name, r.claimant_last_name].filter(Boolean).join(" ") || r.claimant_name || "Unknown";

/** "2 armed, 1 expected" (#3512 AC2, #7361). */
const conditionCountsText = (r: LaseredClaimantListItem): string =>
  conditionStateCountsText(r.lasered_conditions) || "—";

const CONDITIONS_COLUMN_HINT =
  'Counts by trigger state; "expected" is an expected trigger set ahead of the claim line. Under "If triggered": the change to the claimant\'s deductible if that armed condition triggers, against the current effective deductible.';

/**
 * Conditional lasers on a row (#3512 AC2 counts, #3514 option b "If triggered"),
 * one cell so the grid fits a laptop width (UX review #2). For each ARMED
 * condition, how the deductible would move if it triggered, against the row's
 * effective deductible (the straight laser when the server has not resolved
 * one yet). Signed, whole dollars, right-aligned; triggered and inert
 * conditions are left out. With neither (a conditions-only claimant not yet
 * refreshed) the change is unknown: "—", not a change measured from $0.
 */
const ConditionalLasersCell = ({ row }: { row: LaseredClaimantListItem }) => {
  const armed = (row.lasered_conditions ?? []).filter((c) => c.lasered_condition_trigger_state === "armed");
  const current = row.effective_specific_deductible ?? (hasStraightLaser(row) ? row.amt_lasered_deductible : null);
  return (
    <Stack spacing={0.25} data-testid={`conditional-lasers-${row.id}`}>
      <Typography variant="body2" component="span">
        {conditionCountsText(row)}
      </Typography>
      {armed.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">
            If triggered
          </Typography>
          <Stack spacing={0.25} data-testid={`if-triggered-${row.id}`}>
            {armed.map((c) => (
              <Stack
                key={c.id}
                direction="row"
                justifyContent="space-between"
                spacing={1}
                sx={{ whiteSpace: "nowrap" }}
              >
                <Typography variant="body2" component="span">{`${c.lasered_condition_name}: `}</Typography>
                <Typography variant="body2" component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {current === null || current === undefined
                    ? "—"
                    : formatSignedUSD(Math.round(c.amt_lasered_conditional_deductible - current))}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

/**
 * Dedicated tab for managing lasered claimants on the Policy show page.
 * Lists all lasered claimants for the policy with add/edit/delete actions.
 */
export const LaseredClaimantsTab = () => {
  const policy = useRecordContext<Policy>();
  const policyId = policy?.id as string;
  const clientId = policy?.client_id_ref;
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { triggerRecomputeRefresh } = useRecomputeRefreshOrStandalone();

  const resource = `policies/${policyId}/lasered-claimants`;

  const {
    data: laseredClaimants = [],
    isLoading,
    refetch,
  } = useGetList<LaseredClaimantListItem>(
    resource,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    },
    { enabled: !!policyId }
  );

  // Capability gate (Feature #3502): an API without conditional lasers omits
  // `lasered_conditions` on every row. Hide the conditional-laser column then,
  // so the SPA is safe to deploy ahead of the API.
  const supportsConditions = laseredClaimants.some((r) => r.lasered_conditions !== undefined);

  const listContext = useList({
    data: laseredClaimants,
    isLoading,
  });

  // ── Dialog state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editRecord, setEditRecord] = useState<LaseredClaimantListItem | undefined>();

  // ── Delete confirmation state ──
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LaseredClaimantListItem | null>(null);
  const deleteTargetName = deleteTarget ? rowName(deleteTarget) : "this claimant";
  const [deleting, setDeleting] = useState(false);

  const handleAdd = useCallback(() => {
    setDialogMode("create");
    setEditRecord(undefined);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((record: LaseredClaimantListItem) => {
    setDialogMode("edit");
    setEditRecord(record);
    setDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // The whole laser, whatever its type (PO 2026-10-07).
      await removeLaseredClaimant(policyId, String(deleteTarget.id), {
        includeConditions: true,
        includeContract: true,
      });
      notify("Lasered status removed", { type: AlertType.SUCCESS });
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      void refetch();
      // Effective deductibles and stop-loss recompute asynchronously.
      triggerRecomputeRefresh({ notifyUser: false });
    } catch (err) {
      notifyError(err);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, policyId, notify, notifyError, refetch, triggerRecomputeRefresh]);

  const handleDialogSuccess = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleExport = useCallback(() => {
    void downloadLaseredClaimantsReport(laseredClaimants, policyId);
  }, [laseredClaimants, policyId]);

  // Column choices persist per user. Not "policies.datagrid" (the policy list's
  // key), and a separate key without the conditional column, so stored column
  // indexes never point at the wrong column when the API gains it. "v2": the
  // incremental-deductible column is gone (PO 2026-10-07).
  const preferenceKey = `policies.laseredClaimants.v2${supportsConditions ? "" : ".legacy"}.datagrid`;

  const actions = (
    <Stack direction="row" spacing={1} alignItems="center">
      {laseredClaimants.length > 0 && <SelectColumnsButton preferenceKey={preferenceKey} />}
      {laseredClaimants.length > 0 && (
        <Button
          variant={ButtonStyleType.Outlined}
          size={UIComponentSize.Small}
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export
        </Button>
      )}
      <Button
        variant={ButtonStyleType.Outlined}
        size={UIComponentSize.Small}
        startIcon={<AddIcon />}
        onClick={handleAdd}
      >
        Add
      </Button>
    </Stack>
  );

  if (isLoading) {
    return (
      <TabContent>
        <Box />
      </TabContent>
    );
  }

  return (
    <TabContent>
      <ContractLasersSummaryCard policyId={policyId} />
      <ShowAccordionSection label="Lasered Claimants" defaultExpanded actions={actions}>
        {laseredClaimants.length === 0 ? (
          <Box data-testid="lasered-claimants-empty">
            <ListEmptyState icon={GpsFixedIcon} iconSize={32} message="No lasered claimants for this policy." />
          </Box>
        ) : (
          // width 0 + min-width 100%: the grid's own width does not widen the
          // page, so the header and tab actions stay put and only the grid
          // scrolls sideways (UX review #2).
          <Box sx={{ width: 0, minWidth: "100%", overflowX: "auto" }}>
            <ListContextProvider value={listContext}>
              <StyledDatagridConfigurable
                preferenceKey={preferenceKey}
                omit={["contract_period"]}
                bulkActionButtons={false}
                size="small"
                rowClick={(_id, _resource, row) => `/claimants/${row.id}/show`}
                sx={{
                  // Two-line headers ("Effective / Deductible"): the header text, not
                  // the values, set these columns' widths, and one-line headers
                  // pushed the grid past a 1440px screen.
                  "& .RaDatagrid-headerCell": { whiteSpace: "normal", verticalAlign: "bottom" },
                  // Narrower than that, Edit / Remove stay in view while the grid
                  // scrolls sideways. The shared style clips the table (overflow
                  // hidden), which would make it the sticky cells' scroll container.
                  "& .RaDatagrid-table": { overflow: "visible" },
                  "& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type": {
                    position: "sticky",
                    right: 0,
                    zIndex: 1,
                    bgcolor: "background.paper",
                  },
                }}
              >
                <FunctionField label="Name" render={(record: LaseredClaimantListItem) => rowName(record)} />
                <TextField source="member_id" label="Member ID" emptyText="—" />
                <FunctionField
                  label="Laser Type"
                  render={(record: LaseredClaimantListItem) => (
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <LaserChipWithReview
                        variant="icon"
                        pending={record.is_deductible_review_pending}
                        claimantId={String(record.id)}
                        policyId={policyId}
                        claimantName={rowName(record)}
                      />
                      <Typography variant="body2" component="span" data-testid={`laser-type-${record.id}`}>
                        {laserKindLabel(record) || "—"}
                      </Typography>
                    </Stack>
                  )}
                />
                <FunctionField
                  label="Reason / Condition"
                  render={(record: LaseredClaimantListItem) => {
                    if (record.laser_type === "condition_based" && record.conditions?.qualifying_condition)
                      return formatToTitleCase(record.conditions.qualifying_condition as string);
                    if (record.laser_type === "individual" && record.laser_reason) return record.laser_reason;
                    return "—";
                  }}
                />
                <FunctionField
                  label="Lasered Deductible"
                  textAlign="right"
                  render={(record: LaseredClaimantListItem) =>
                    // Conditions-only rows have no straight laser; their amount (often 0) is not one.
                    hasStraightLaser(record) ? formatUSD(record.amt_lasered_deductible, NO_DECIMALS) : "—"
                  }
                />
                <FunctionField
                  label="Effective Deductible"
                  textAlign="right"
                  render={(record: LaseredClaimantListItem) => {
                    if (record.effective_specific_deductible === undefined) return "—";
                    return (
                      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                        <DeductibleSourceChip source={record.effective_deductible_source} />
                        <span>{formatUSD(record.effective_specific_deductible, NO_DECIMALS)}</span>
                      </Stack>
                    );
                  }}
                />
                {supportsConditions && (
                  <FunctionField
                    source="lasered_conditions"
                    sortable={false}
                    label={
                      <Box
                        component="span"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                        data-testid="conditional-lasers-column"
                      >
                        Conditional Lasers
                        <HintIcon hint={CONDITIONS_COLUMN_HINT} variant="info" />
                      </Box>
                    }
                    render={(record: LaseredClaimantListItem) => <ConditionalLasersCell row={record} />}
                  />
                )}
                <FunctionField
                  label="Contract Deductible"
                  textAlign="right"
                  render={(record: LaseredClaimantListItem) => {
                    const cl = record.contract_laser;
                    // Fully-excluded claimants have no specific eligibility → not applicable.
                    if (!cl?.is_contract_lasered || cl.is_contract_excluded) return "—";
                    return cl.amt_contract_lasered_deductible
                      ? formatUSD(cl.amt_contract_lasered_deductible, NO_DECIMALS)
                      : "—";
                  }}
                />
                <FunctionField
                  source="contract_period"
                  label="Contract Period"
                  sortable={false}
                  render={(record: LaseredClaimantListItem) => {
                    const cl = record.contract_laser;
                    if (!cl?.is_contract_lasered || cl.is_contract_excluded) return "—";
                    const start = cl.contract_incurred_start_date;
                    const end = cl.contract_incurred_end_date;
                    if (!start && !end) return "—";
                    return `${start ? formatDate(start) : "—"} – ${end ? formatDate(end) : "—"}`;
                  }}
                />
                <FunctionField
                  label="Actions"
                  textAlign="right"
                  render={(record: LaseredClaimantListItem) => (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(record);
                          }}
                          sx={{ color: "text.secondary" }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(record);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ color: "text.secondary" }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                />
              </StyledDatagridConfigurable>
            </ListContextProvider>
          </Box>
        )}
      </ShowAccordionSection>

      {/* Create/Edit dialog */}
      <LaseredClaimantDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        policyId={policyId}
        clientId={clientId}
        policyEffectiveStart={policy?.effective_start_date}
        policyEffectiveEnd={policy?.effective_end_date}
        specificBenefit={policy?.specific_benefit}
        mode={dialogMode}
        existingRecord={editRecord}
        onSuccess={handleDialogSuccess}
        expectedTriggers
      />

      {/* Delete confirmation */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Remove Lasered Status"
        message={`Remove the lasered status from ${deleteTargetName}? ${REMOVE_LASER_OUTCOME}`}
        confirmLabel={ConfirmLabelType.Remove}
        confirmColor={ColorType.Error}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={deleting}
      >
        <RemoveLaserSummary
          straightAmount={
            deleteTarget && hasStraightLaser(deleteTarget) ? deleteTarget.amt_lasered_deductible : undefined
          }
          conditions={deleteTarget?.lasered_conditions ?? []}
          contract={hasContractLaser(deleteTarget?.contract_laser) ? deleteTarget?.contract_laser : undefined}
          reviewPending={!!deleteTarget?.is_deductible_review_pending}
        />
      </ConfirmationDialog>
    </TabContent>
  );
};
